import React from 'react';
import PropTypes from 'prop-types';
import BucketsInput from '../BucketsInput';
import ConditionBuilder from '../ConditionBuilder';
import TimePicker from '../TimePicker';
import LinkValue from './LinkValue';
import DropdownValue from './DropdownValue';
import MultiselectValue from './MultiselectValue';
import NestedMultiselectValue from './NestedMultiselectValue';
import IncludeMultiselectValue from './IncludeMultiselectValue';
import NumberInputValue from './NumberInputValue';
import StaticTextValue from './StaticTextValue';
import ComparisonValue from './ComparisonValue';
import RangeValue from './RangeValue';
import IncludeExcludeMultiselectValue from './IncludeExcludeMultiselectValue';
import SearchInputValue from './SearchInputValue';
import { MAX_CONDITIONS_PER_FIELD } from '../../fieldsConfig';
import { useEligibilityActions } from '../../../../hooks/useEligibilityEngine';
import APP_PATH from '../../../../constants/paths';
import { safeParseJson } from '../../../../utilities/jsonUtil';

/**
 * Default multiselect adapters, used when a `multiselect` rule declares no
 * `mapToComponentFromStore`/`mapToStoreFromComponent`. They assume the standard
 * stored contract:
 *   { "types": [ { "name": "...", "enabled": true }, ... ] }
 * `mapToComponentFromStore` exposes every type name as an option and pre-selects
 * the enabled ones; `mapToStoreFromComponent` preserves the original `types`
 * array and only flips each entry's `enabled` flag to match the current selection.
 */
const defaultMultiselectMapToComponentFromStore = value => {
  const types = safeParseJson(value, {}).types || [];
  return {
    options: types.map(type => type.name),
    value: JSON.stringify(
      types.filter(type => type.enabled).map(type => type.name)
    ),
  };
};

const defaultMultiselectMapToStoreFromComponent = (newValue, rule) => {
  const types = safeParseJson(rule.value, {}).types || [];
  const selected = safeParseJson(newValue, []);
  return JSON.stringify({
    types: types.map(type => ({
      ...type,
      enabled: selected.includes(type.name),
    })),
  });
};

const ValueSelector = ({ rule }) => {
  const { onValueChange, onAddCondition } = useEligibilityActions();

  // --- Rules hard-coded by id (no generic valueType contract applies) ---
  switch (rule.id) {
    case 'vinBlacklistEligibilityRule':
      return (
        <LinkValue
          valueLinkText='automatically fetched from the VIN Blacklist, to edit go'
          valueLinkUrl={APP_PATH.VIN_BLACKLISTS}
        />
      );

    default:
      break;
  }

  // --- Generic rules (switch on valueType from FIELDS_CONFIG) ---
  switch (rule.valueType) {
    case 'link':
      return (
        <LinkValue
          valueLinkText={rule.valueLinkText}
          valueLinkUrl={rule.valueLinkUrl}
        />
      );

    case 'multiselect': {
      // Rules may declare mapToComponentFromStore/mapToStoreFromComponent adapters
      // (in FIELDS_CONFIG) to map the stored JSON value with rule-specific keys
      // to/from the multiselect contract. When a rule omits them, assume the
      // standard `{ types: [{ name, enabled }] }` contract via the default adapters.
      const mapToComponentFromStore = rule.mapToComponentFromStore
        || defaultMultiselectMapToComponentFromStore;
      const mapToStoreFromComponent = rule.mapToStoreFromComponent
        || defaultMultiselectMapToStoreFromComponent;
      const adaptedRule = { ...rule, ...mapToComponentFromStore(rule.value) };
      const handleMultiselectChange = (id, newValue) => onValueChange(id, mapToStoreFromComponent(newValue, rule));

      return (
        <MultiselectValue
          rule={adaptedRule}
          onValueChange={handleMultiselectChange}
        />
      );
    }

    case 'nested_multiselect': {
      // Two-level multiselect over a flat [{ id, groupName, name, checked }]
      // list. Like `multiselect`, a rule may declare map* adapters in
      // FIELDS_CONFIG to map its stored JSON to/from that contract; they get the
      // whole `rule`, so a catalog served alongside the value (e.g.
      // `allowedChannels`) is reachable. Without adapters the value passes through.
      const mapToComponentFromStore = rule.mapToComponentFromStore
        || (value => ({ value }));
      const mapToStoreFromComponent = rule.mapToStoreFromComponent
        || (newValue => newValue);
      const adaptedRule = { ...rule, ...mapToComponentFromStore(rule.value, rule) };
      const handleNestedChange = (id, newValue) => onValueChange(id, mapToStoreFromComponent(newValue, rule));

      return (
        <NestedMultiselectValue
          rule={adaptedRule}
          onValueChange={handleNestedChange}
        />
      );
    }

    case 'range': {
      // Rules may declare mapToComponentFromStore/mapToStoreFromComponent adapters
      // (in FIELDS_CONFIG) to map the stored JSON value with rule-specific keys
      // to/from the range contract.
      const adaptedRule = rule.mapToComponentFromStore
        ? { ...rule, ...rule.mapToComponentFromStore(rule.value) }
        : rule;
      const handleRangeChange = (id, value) => onValueChange(
        id,
        rule.mapToStoreFromComponent ? rule.mapToStoreFromComponent(value) : value
      );

      return (
        <RangeValue
          rule={adaptedRule}
          onValueChange={handleRangeChange}
        />
      );
    }

    case 'buckets':
      return (
        <BucketsInput
          buckets={rule.buckets}
          ruleId={rule.instanceId}
          onValueChange={onValueChange}
        />
      );

    case 'include_multiselect':
      return (
        <IncludeMultiselectValue
          rule={rule}
          onValueChange={onValueChange}
        />
      );

    case 'number_input': {
      // Rules may declare mapToComponentFromStore/mapToStoreFromComponent adapters
      // (in FIELDS_CONFIG) to map the stored JSON value with rule-specific keys
      // to/from the numberValue contract. When a rule does not declare them, fall
      // back to a pass-through that keeps the numberValue contract in sync with
      // the persisted value.
      const mapToComponentFromStore = rule.mapToComponentFromStore
        || (value => ({ numberValue: value }));
      const mapToStoreFromComponent = rule.mapToStoreFromComponent
        || (numberValue => numberValue);
      const adaptedRule = { ...rule, ...mapToComponentFromStore(rule.value) };
      const handleNumberChange = (id, val) => onValueChange(id, mapToStoreFromComponent(val));

      return (
        <NumberInputValue
          rule={adaptedRule}
          onValueChange={handleNumberChange}
        />
      );
    }

    case 'static_text':
      return <StaticTextValue ruleId={rule.id} staticText={rule.staticText} />;

    case 'comparison_value': {
      // Calculate current condition count and effective max
      const effectiveMax = rule.maxConditions ?? MAX_CONDITIONS_PER_FIELD;
      const currentCount = rule.conditions?.length ?? 1;
      const canAddCondition = rule.allowAddCondition && currentCount < effectiveMax;

      return (
        <>
          <ComparisonValue
            rule={rule}
            onValueChange={onValueChange}
          />
          {canAddCondition && (
            <a
              href='#'
              className='add-condition-link'
              onClick={e => {
                e.preventDefault();
                onAddCondition(rule.instanceId);
              }}
              aria-label='Add condition'
              title='Add condition'
            >
              + Add condition
            </a>
          )}
        </>
      );
    }

    case 'include_exclude_multiselect':
      return (
        <IncludeExcludeMultiselectValue
          rule={rule}
          onValueChange={onValueChange}
        />
      );

    case 'condition_builder': {
      const effectiveMax = rule.maxConditions ?? MAX_CONDITIONS_PER_FIELD;
      return (
        <ConditionBuilder
          conditions={rule.conditions}
          ruleId={rule.instanceId}
          onValueChange={onValueChange}
          allowAddCondition={rule.allowAddCondition}
          maxConditions={effectiveMax}
          onAddCondition={onAddCondition}
        />
      );
    }

    case 'search_input':
      return (
        <SearchInputValue
          rule={rule}
          onValueChange={onValueChange}
        />
      );

    case 'time_picker':
      return (
        <TimePicker
          rule={rule}
          onValueChange={onValueChange}
          allowAddCondition={rule.allowAddCondition}
        />
      );

    case 'dropdown':
      return (
        <DropdownValue
          rule={rule}
          onValueChange={onValueChange}
        />
      );

    case 'empty':
      return null;

    default:
      return null;
  }
};

ValueSelector.propTypes = {
  rule: PropTypes.object.isRequired,
};

export default ValueSelector;
